import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import { useGetMedicationsQuery } from "src/api/services/medicationsApi";
import { filters } from "./utils/filters";
import TableFilter from "src/components/TableFilter";
import { useSearchParams } from "react-router-dom";
import { toNumber } from "src/utils/toNumber";
import {
  DISPENSE_UNIT,
  DOSAGE,
  GENDER,
  LIMIT,
  MEDICATION,
  MEDICATION_FORM,
  ORDER,
  ORDER_BY,
  PAGE,
  PHARMACY,
  TREATMENT,
} from "src/constants/searchParams";
import Pharmacies from "./components/Pharmacies";
import { Button } from "@mui/material";
import { useManageModal } from "src/hooks/useManageModal";
import CreateMedicationModal from "../../components/CreateMedicationModal";
import { Add } from "@mui/icons-material";
import DataTable from "src/components/DataTable";
import { useColumns } from "./utils/useColumns";
import { usePagination } from "src/hooks/usePagination";

const Medications = () => {
  const [params] = useSearchParams();
  const page = useMemo(() => toNumber(params.get(PAGE)), [params]);
  const limit = useMemo(() => toNumber(params.get(LIMIT)), [params]);
  const [open, { handleClose, handleOpen }] = useManageModal();
  const columns = useColumns();
  const pagination = usePagination();

  const height = useMemo(() => window.innerHeight - 104 - 96 - 32 - 4 - 48, []);

  const from = useMemo(() => {
    return page * limit;
  }, [limit, page]);

  const to = useMemo(() => {
    return from + limit - 1;
  }, [from, limit]);

  const queryParams = useMemo(
    () => ({
      order_by: params.get(ORDER_BY) ?? undefined,
      ascending: params.get(ORDER) === "asc" ? true : false,
      pharmacy: params.get(PHARMACY) ?? undefined,
      dispense_unit: params.get(DISPENSE_UNIT) ?? undefined,
      name: params.get(MEDICATION) ?? undefined,
      form: params.get(MEDICATION_FORM) ?? undefined,
      treatment: params.get(TREATMENT) ?? undefined,
      dosage: params.get(DOSAGE) ?? undefined,
      gender: params.get(GENDER) ?? undefined,
      from,
      to,
    }),
    [from, params, to],
  );
  const { data, isFetching } = useGetMedicationsQuery(queryParams);

  const { medications, count } = useMemo(() => {
    if (data == null) return { medications: [], count: 0 };
    return { medications: data.data, count: data.count };
  }, [data]);

  return (
    <Stack gap="32px">
      <Typography variant="h4" fontWeight={600}>
        Medications
      </Typography>

      <Pharmacies />

      <Stack direction={"row"}>
        <Stack
          direction="row"
          gap="16px"
          height={60}
          width="100%"
          alignItems="center"
        >
          {filters.map((filter) => (
            <TableFilter key={filter.name} current={filter} />
          ))}
        </Stack>

        <Button variant={"contained"} onClick={handleOpen}>
          <Add /> Add
        </Button>
      </Stack>
      <Stack gap={0}>
        <Box height={4}>
          {isFetching ? <LinearProgress color="secondary" /> : null}
        </Box>
        <DataTable
          data={medications}
          columns={columns}
          primaryKey="id"
          height={height - 52}
          pagination={pagination}
          total={count}
        />
      </Stack>
      <CreateMedicationModal open={open} onClose={handleClose} />
    </Stack>
  );
};

export default Medications;
